import React, { FC, Suspense, lazy, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import type {
    GetRealmQuery,
    Realm as RealmFields,
    Zone as ZoneFields,
    CreateZoneInput as NewNodeFields,
} from '@/generated/graphql';
import GetRealm from './graphql/Realm.gql';
import StageShell from '@/components/stage/_common/StageShell/StageShell';
import { useUserCurrent } from '@/components/provider/UserCurrentProvider';
import Loading from '@/components/basic/Loading/Loading';
import { concatenateIdToPath, markContentReady } from '@/utils';
import Lite from '../_common/Lite/Lite';
import { getInitialPhotoData } from '@/components/edit/media/PhotoInput/PhotoInput';

const EditShell = lazy(
    () =>
        import(
            /* webpackChunkName: "edit-main" */ '@/components/edit/EditShell/EditShell'
        ),
);
const RealmEdit = lazy(
    () => import(/* webpackChunkName: "edit-main" */ './RealmEdit'),
);

type RealmProps = {
    realmId: number;
};

const Realm: FC<RealmProps> = ({ realmId }) => {
    const { userCurrent } = useUserCurrent();
    const { loading, error, data } = useQuery<GetRealmQuery>(GetRealm, {
        variables: { realmId },
    });
    useEffect(() => {
        if (!loading && !error && !!data) markContentReady();
    }, [loading, error, data]);

    if (loading) {
        return <Loading type="element" />;
    }

    if (error || !data) {
        return <pre>Error...{JSON.stringify(error, null, 2)}</pre>;
    }

    const realm: RealmFields = data.realm;

    const currentNodes = [];
    const zones = realm.zones.map((zone: ZoneFields, index) => {
        currentNodes.push({
            nodeId: zone.id,
            title: zone.title,
        });
        return (
            <Lite
                key={`zone${index}`}
                fields={zone}
                path={concatenateIdToPath({ id: zone.id, pathId: zone.pathId })}
            />
        );
    });

    const stageShellContent = (
        <StageShell
            componentName="Realm"
            fields={realm}
            canonicalPath={[realm.pathId]}
        >
            {zones}
        </StageShell>
    );

    if (userCurrent?.isEditMode === 'REALM') {
        const nodeIds = currentNodes.map((node) => node.nodeId);

        const passEditState = {
            editKey: realmId,
            componentType: 'REALM',
            fields: {
                published: realm.published,
                publishAll: false,
                autoUpdatePathId: false,
            },
            localFields: {
                title: realm.title,
                pathId: realm.pathId,
                bannerText: realm.bannerText,
                bannerPhoto: {
                    id: realm.bannerPhoto?.[0]?.id,
                    caption: realm.bannerPhoto?.[0]?.caption,
                    copyright: realm.bannerPhoto?.[0]?.copyright?.id,
                    copyrightUrl: realm.bannerPhoto?.[0]?.copyrightUrl,
                    imageFile: null,
                },
            },
            realmId: realmId,
            publishedLocked: realm.publishedLocked,
            ownerId: realm.owner.id,

            newNodeFields: {
                bannerPhoto: getInitialPhotoData(),
                matrix: { bannerPhoto: getInitialPhotoData() },
            } as NewNodeFields,

            // needed for deleting and sorting zones
            currentNodes: currentNodes,

            nodesFlaggedForRemoval: [],
            nodesFlaggedForUnlinking: [],

            // for sorting
            nodeIds: nodeIds, // must remain unchanged for comparison.
            sortedNodeIds: nodeIds,
            hasResortedNodes: false,
        };

        return (
            <Suspense fallback={stageShellContent}>
                <EditShell passEditState={passEditState}>
                    <RealmEdit realm={realm} zones={zones} />
                </EditShell>
            </Suspense>
        );
    }

    return stageShellContent;
};

export default Realm;
